// https://caniuse.com/#feat=mdn-api_clipboard
// https://github.com/sindresorhus/copy-text-to-clipboard
import copyTextToClipboard from 'copy-text-to-clipboard';
import {SAFARI} from '@packages/utilities/environment';

const errorResponse = {type: 'error'} as const;
const successResponse = {type: 'success'} as const;
const unauthorizedResponse = {type: 'unauthorized'} as const;
const PERMISSIONS = ['granted', 'prompt'];

/**
 * A promise-based clipboard utility that writes content to a user's clipboard.
 * Below is an example usage in a React component, but can be used in any
 * utility that requires it.
 *
 * ## Example usage
 *
 * ```jsx
 * import {copyToClipboard} from '@packages/utilities/clipboard-utils';
 *
 * const MyComponent = () => {
 *   const [response, setResponse] = useState(null);
 *
 *   const onClick = async () => {
 *     const myContent = JSON.stringify(
 *       {one: 1, two: [{three: 3, four: {five: 5}}]},
 *       null,
 *       2
 *     );
 *
 *     const result = await copyToClipboard(myContent);
 *
 *     setResponse(result);
 *   };
 *
 *   return (
 *     <>
 *       <button onClick={onClick}>
 *         Click me to copy the JSON above to clipboard!
 *       </button>
 *
 *       {response && response.type === 'success' && 'Copy successful!'}
 *
 *       <code>
 *         <pre>{response && response.type === 'success' && content}</pre>
 *       </code>
 *     </>
 *   );
 * };
 */
export async function copyToClipboard(
  clipboardContent: string
): Promise<Response> {
  if (SAFARI) {
    try {
      await window.navigator.clipboard.writeText(clipboardContent);
      return successResponse;
    } catch (e: any) {
      return errorResponse;
    }
  }
  // Try to use the native clipboard API first if it exists
  if (window.navigator.permissions && window.navigator.clipboard) {
    // Querying the "clipboard-write" permission name is not supported in
    // Firefox, which throws instead of resolving:
    // https://developer.mozilla.org/en-US/docs/Web/API/Permissions_API#browser_compatibility
    // Firefox does support `clipboard.writeText` itself though, so fall
    // through to attempting the write directly rather than giving up.
    let state: PermissionState | undefined;
    try {
      ({state} = await window.navigator.permissions.query({
        // "clipboard-write" is not listed in TypeScript's types for the
        // permission API because it is still experimental.
        name: 'clipboard-write' as PermissionName,
      }));
    } catch (e: any) {
      // permissions.query rejected, so `state` stays undefined.
    }

    if (state === undefined || PERMISSIONS.includes(state)) {
      try {
        await window.navigator.clipboard.writeText(clipboardContent);
        return successResponse;
      } catch (e: any) {
        return errorResponse;
      }
    } else {
      return unauthorizedResponse;
    }
  }

  // Otherwise fallback to the previous method
  return copyTextToClipboard(clipboardContent)
    ? successResponse
    : errorResponse;
}

export type Response = {
  type: 'success' | 'error' | 'unauthorized';
};
